import { getPrepaymentDate, getTodaysDate } from './helpers/getPrepaymentDate'
import hasPaymentDatePassed from './helpers/hasPaymentDatePassed'
import createLogger from './helpers/scheduledPaymentsLogger'
import validateScheduledPaymentsData from './helpers/scheduledPaymentsValidator'
import { DIRECT, FUTURE } from 'Rentlio/utils/scheduledPaymentTypeEnum'
import { FIRST_NIGHT } from 'Rentlio/utils/CancellationPolicyFeeTypeEnum'

/*
/////////////////////////////////////////////////////////////////////////////////////////////
//                                                                                         //
//   ██████╗ ███████╗███╗   ██╗████████╗██╗     ██╗ ██████╗      ██████╗  █████╗ ██╗   ██╗ //
//   ██╔══██╗██╔════╝████╗  ██║╚══██╔══╝██║     ██║██╔═══██╗     ██╔══██╗██╔══██╗╚██╗ ██╔╝ //
//   ██████╔╝█████╗  ██╔██╗ ██║   ██║   ██║     ██║██║   ██║     ██████╔╝███████║ ╚████╔╝  //
//   ██╔══██╗██╔══╝  ██║╚██╗██║   ██║   ██║     ██║██║   ██║     ██╔═══╝ ██╔══██║  ╚██╔╝   //
//   ██║  ██║███████╗██║ ╚████║   ██║   ███████╗██║╚██████╔╝     ██║     ██║  ██║   ██║    //
//   ╚═╝  ╚═╝╚══════╝╚═╝  ╚═══╝   ╚═╝   ╚══════╝╚═╝ ╚═════╝      ╚═╝     ╚═╝  ╚═╝   ╚═╝    //
//                                                                                         //
/////////////////////////////////////////////////////////////////////////////////////////////
//  The best payment solution for hotels
*/

// This function is used not only for scheduling payments but also for displaying billing dynamics amounts

// DIRECT payment is paid immediately
// FUTURE payment is charged automatically with our script
// excludeFromScheduling flag is used to avoid payment from being scheduled for automatic payment (if a payment is set to be paid during stay, we don't know at which time user wants it to be charged so that payment must be performed manually)
//
//
//
// Billing dynamics possibilities:
//
// partially at the time of booking and partially x days before arrival -> one DIRECT payment and one FUTURE payment
// partially at the time of booking and partially during stay -> one DIRECT payment and one FUTURE payment with excludeFromScheduling set to true
// partially x days before arrival and partially y days before arrival (where x>y) -> two FUTURE payments
// partially x days before arrival and partially during stay -> one FUTURE payment and one FUTURE payment with excludeFromScheduling set to true
// ->  more posibilities if dates are in the past or today:
//        partially x days before arrival and partially y days before arrival (where x>y and x is in past/today) -> one DIRECT payment and one FUTURE payment
//        partially x days before arrival and partially y days before arrival (where x>y and x and y is in past/today) -> one DIRECT payment
//        partially x days before arrival and partially during stay (x is in past/today) -> one DIRECT payment and one FUTURE payment with excludeFromScheduling set to true
//        partially x days before arrival and partially during stay (arrival date is today, this was the problem with isAmountWillBePaidDaysBeforeInThePast being true) -> one DIRECT payment and one FUTURE payment with excludeFromScheduling set to true

// full payment at the time of booking -> one DIRECT payment
// full payment x days before arrival -> one FUTURE payment
// full payment x days before arrival (x is in the past/today) -> one DIRECT payment
// full payment during stay (arrival date is in the future) -> one FUTURE payment with excludeFromScheduling set to true
// full payment during stay (arrival date is today/past) -> one DIRECT payment with excludeFromScheduling set to true, proceed with the card tokenization process (we just want to get the card token without any automatic payments)
//
// special case where prepaymenyAmountPercentage is 100, prepayment x days before arrival (x is in the past/today) -> one DIRECT payment (special case where amountWillBePaidDaysBefore is 0 but it doesnt mean that remaining amount will be paid during stay because prepaymenyAmountPercentage is 100)

// All these posibilities are covered in getFuturePayments.test.js
export const getFuturePayments = (
  arrival,
  total,
  currency,
  amountWillBePaidDaysBefore,
  prepaymentAmountType,
  prepaymenyAmountPercentage,
  prepaymentAmountDueDate,
  firstNightPrice,
  dateToday // This is used only for testing (getCancellationPolicyText.test.js heavily relies on it, so I wont be mocking it for now)
) => {
  // group all arguments received in this function to avoid repetition
  const params = {
    arrival,
    total,
    currency,
    amountWillBePaidDaysBefore,
    prepaymentAmountType,
    prepaymenyAmountPercentage,
    prepaymentAmountDueDate,
    firstNightPrice,
    dateToday
  }

  // Define logger to avoid repetition. When used it will log all the arguments passed to this function for easier debugging
  const logger = createLogger(params)

  const prepaymentDate = getPrepaymentDate(arrival, prepaymentAmountDueDate)
  const remainingPaymentDate = getPrepaymentDate(arrival, amountWillBePaidDaysBefore)
  const todaysDate = getTodaysDate()

  const isPrepaymentInThePast = hasPaymentDatePassed(arrival, prepaymentAmountDueDate, dateToday)
  const isAmountWillBePaidDaysBeforeInThePast = hasPaymentDatePassed(arrival, amountWillBePaidDaysBefore, dateToday)

  const prepayment = prepaymentAmountType === FIRST_NIGHT ? firstNightPrice : total * (prepaymenyAmountPercentage / 100)
  const remainingAmount = total - prepayment

  const hasBlockingError = validateScheduledPaymentsData(logger, {
    ...params,
    prepaymentDate,
    remainingPaymentDate,
    isPrepaymentInThePast,
    isAmountWillBePaidDaysBeforeInThePast,
    remainingAmount
  })

  if (hasBlockingError) {
    // return empty arrays for scheduling payments, we dont want to schedule payments if application is in unexpected state
    return []
  }

  // If remaining amount will be paid during stay then amountWillBePaidDaysBefore is set to 0
  // But there is a case where amountWillBePaidDaysBefore is also set to 0 and that is when prepaymenyAmountPercentage is 100 (ther is no remaining amount), so we check for that case
  const remainingAmountWillBePaidDuringStay = +amountWillBePaidDaysBefore <= 0 && prepaymenyAmountPercentage !== 100

  // merging payments is not possible if prepayment > 0 and remainingAmountWillBePaidDuringStay is true,
  //  because remaining amount in that case should be excluded from scheduling and prepayment is not excluded

  const canMergePayments =
    isAmountWillBePaidDaysBeforeInThePast &&
    (!remainingAmountWillBePaidDuringStay || (remainingAmountWillBePaidDuringStay && remainingAmount === total))

  // full amount should be paid immediately if it is not set to be paid during stay
  // if it is set to be paid during stay (amountWillBePaidDaysBefore === 0) then exclude it from scheduling
  if (isAmountWillBePaidDaysBeforeInThePast && canMergePayments) {
    return [
      {
        amount: total,
        amountCents: total * 100,
        currency: currency,
        type: DIRECT,
        timestamp: todaysDate,
        excludeFromScheduling: remainingAmountWillBePaidDuringStay // If the amount is set to be paid during the stay, it will not be scheduled for automatic payment.
      }
    ]
  }

  const isDirect = isPrepaymentInThePast || prepaymentAmountDueDate === 0

  const payments = []

  // If remaining amount is 0 then only second payment will be scheduled
  if (prepayment > 0) {
    payments.push({
      amount: prepayment,
      amountCents: prepayment * 100,
      currency: currency,
      type: isDirect ? DIRECT : FUTURE,
      timestamp: isDirect ? todaysDate : prepaymentDate,
      excludeFromScheduling: false
    })
  }

  // If remaining amount is 0 then only first payment will be scheduled
  if (remainingAmount > 0) {
    payments.push({
      amount: remainingAmount,
      amountCents: remainingAmount * 100,
      currency: currency,
      type: FUTURE,
      timestamp: remainingPaymentDate,
      excludeFromScheduling: remainingAmountWillBePaidDuringStay // If the remaining amount is set to be paid during the stay, it will not be scheduled for automatic payment.
    })
  }

  return payments
}
